/**
 * Comprehensive Sources Management
 * Full implementation of all Source API endpoints
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import {
  listSources,
  deleteSource,
  triggerSync,
  getSyncStatus,
  triggerFileUpload,
  registerAPIIntegrationSource,
} from '../../api/sources';
import { getJob } from '../../api/jobs';
import { useWorkspace } from '../../hooks/useWorkspace';
import { AddSourceModal } from './AddSourceModal';
import { BulkUploadModal } from './BulkUploadModal';
import { DeleteSourceConfirmationModal } from './DeleteSourceConfirmationModal';
import { SourceFileTabs } from './SourceFileTabs';
import {
  formatFileSize,
  buildFileTree,
  SUPPORTED_EXTENSIONS,
  EXTENSION_DESCRIPTIONS,
  type FileNode,
} from './sourceUtils';
import {
  Database,
  Plus,
  RefreshCw,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Play,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  Folder,
  Cloud,
  Globe,
  File,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  HelpCircle,
} from 'lucide-react';

/** Steps to renew SAP Ariba session (cookies/CSRF expire). Shown for api_integration + sap_ariba. */
/** Backend adds metadata.last_sync_job so UI can distinguish Redis backlog vs real failures */
/** True when the worker has a path to scan (UNC/SSH or file/UNC URI); aligns with trigger_file_upload / registration. */
function isServerSideLocalFolder(metadata?: Record<string, any>, uri?: string | null): boolean {
  const conn = metadata?.connection_config;
  const connType =
    typeof conn === 'object' && conn && 'type' in conn
      ? String((conn as { type?: string }).type).toLowerCase()
      : '';
  if (connType === 'unc' || connType === 'ssh') return true;

  const u = (uri ?? '').trim();
  if (!u) return false;
  const lower = u.toLowerCase();
  if (lower.startsWith('client://')) return false;
  return (
    lower.startsWith('unc://') ||
    lower.startsWith('file://') ||
    lower.startsWith('smb://') ||
    lower.startsWith('ssh://') ||
    lower.startsWith('\\\\')
  );
}

/** Browser-registered local folders: API /upload cannot scan disk; use Bulk Upload instead */
function isBrowserOnlyLocalFolder(
  metadata?: Record<string, any>,
  uri?: string | null,
): boolean {
  if (metadata?.client_side === true || metadata?.client_side === 'true') return true;
  if (uri && String(uri).toLowerCase().startsWith('client://')) return true;
  // DB/script-created bulk sources (e.g. "Bulk Ingestion - Missing Documents") have bulk_ingestion + no URI
  if (metadata?.bulk_ingestion === true && !isServerSideLocalFolder(metadata, uri)) return true;
  return false;
}

function getApiIntegrationDisplay(source: {
  sync_status?: string | null;
  last_sync_at?: string | null;
  type: string;
  metadata?: {
    last_sync_job?: { status?: string; error_message?: string | null };
    config_incomplete?: boolean;
  };
}): {
  statusLabel: string;
  badgeVariant: 'success' | 'warning' | 'error' | 'default';
  lastSyncLabel: string;
  hint?: string;
} {
  const job = source.metadata?.last_sync_job;
  if (job?.status === 'failed') {
    return {
      statusLabel: 'failed',
      badgeVariant: 'error',
      lastSyncLabel: source.last_sync_at ? new Date(source.last_sync_at).toLocaleString() : '—',
      hint: job.error_message || 'Sync job failed. Check API/worker logs.',
    };
  }
  if (job?.status === 'queued' && source.sync_status === 'queued') {
    return {
      statusLabel: 'queued',
      badgeVariant: 'warning',
      lastSyncLabel: 'Waiting for worker',
      // hint:
      //   'The sync job is in Redis but no worker has picked it up. Start the TraceOS Celery worker with queues including api_integration_sync (production/on-prem) or api_integration_sync_local (local dev). ENVIRONMENT must match between API and worker so they use the same queue.',
    };
  }
  if (job?.status === 'running') {
    return {
      statusLabel: 'running',
      badgeVariant: 'default',
      lastSyncLabel: source.last_sync_at ? new Date(source.last_sync_at).toLocaleString() : 'In progress',
    };
  }
  if (source.metadata?.config_incomplete) {
    return {
      statusLabel: source.sync_status || 'misconfigured',
      badgeVariant: 'warning',
      lastSyncLabel: source.last_sync_at ? new Date(source.last_sync_at).toLocaleString() : 'Never',
      hint:
        'integration_type is missing in the database for this source. Re-create the integration or set meta.integration_type (e.g. sap_ariba) so the worker uses the correct fetcher.',
    };
  }
  const sync =
    source.sync_status === 'pending' ? 'queued' : source.sync_status || 'unknown';
  return {
    statusLabel: sync,
    badgeVariant:
      source.sync_status === 'active' || source.sync_status === 'completed'
        ? 'success'
        : source.sync_status === 'queued' || source.sync_status === 'pending'
          ? 'warning'
          : source.sync_status === 'running'
            ? 'default'
            : source.sync_status === 'failed'
              ? 'error'
              : 'default',
    lastSyncLabel:
      source.sync_status === 'queued' && source.last_sync_at
        ? 'Queued now'
        : source.last_sync_at
          ? new Date(source.last_sync_at).toLocaleString()
          : 'Never',
  };
}

const SAP_ARIBA_RENEWAL_STEPS = (
  <div className="text-sm text-theme-text space-y-2 list-decimal list-inside">
    <span>Log in to <a href="https://portal.us.bn.cloud.ariba.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">portal.us.bn.cloud.ariba.com</a> in a browser.</span>
    <span>Open DevTools (F12) → Network tab.</span>
    <span>Perform a search in the SAP Ariba portal.</span>
    <span>Select the search request; copy the <code className="px-1 py-0.5 bg-theme-surface-hover rounded">Cookie</code> header and the <code className="px-1 py-0.5 bg-theme-surface-hover rounded">x-csrf-token</code> header.</span>
    <span>Set env vars <code className="px-1 py-0.5 bg-theme-surface-hover rounded">SAP_ARIBA_COOKIES_FRESH</code> and <code className="px-1 py-0.5 bg-theme-surface-hover rounded">SAP_ARIBA_CSRF_TOKEN_FRESH</code> (or update source credentials with <code className="px-1 py-0.5 bg-theme-surface-hover rounded">cookies</code> and <code className="px-1 py-0.5 bg-theme-surface-hover rounded">csrf_token</code>).</span>
    <span>Re-run Sync. Sessions expire periodically; repeat when sync returns 403.</span>
  </div>
);

// Component to display artifact statistics
function SourceArtifactStats({ sourceId, sourceType }: { sourceId: string; sourceType: string }) {
  const { data: syncStatus, isLoading } = useQuery({
    queryKey: ['syncStatus', sourceId],
    queryFn: () => getSyncStatus(sourceId),
    enabled: true,
    refetchInterval: 60000, // Refresh every minute
  });

  const handleNavigateToKnowledgeBase = () => {
    // Navigate to Knowledge Base with source filter
    const url = `/knowledge?source_id=${sourceId}`;
    window.dispatchEvent(new CustomEvent('navigate', { detail: { path: url } }));
  };

  if (isLoading) {
    return (
      <div className="mt-3 pt-3 border-t">
        <div className="text-xs text-theme-text-secondary">Loading stats...</div>
      </div>
    );
  }

  const artifactStats = syncStatus?.metadata?.artifact_stats;
  if (!artifactStats) {
    return null;
  }

  const deltaNewEdited = syncStatus?.metadata?.delta_files_since_last_sync;
  const deltaKeywordMatched = syncStatus?.metadata?.delta_files_matching_keywords;
  const deltaCutoff = syncStatus?.metadata?.delta_cutoff;

  const total = artifactStats.total_artifacts || 0;
  const chunked = artifactStats.artifacts_with_chunks || 0;
  const entities = artifactStats.artifacts_with_entities || 0;
  const chunkedPct = artifactStats.chunked_percent || 0;
  const entitiesPct = artifactStats.entities_percent || 0;

  if (total === 0) {
    return null;
  }

  return (
    <div
      className="mt-3 pt-3 border-t border-theme-border cursor-pointer hover:bg-theme-surface-hover rounded-lg p-2 -m-2 transition-colors"
      onClick={handleNavigateToKnowledgeBase}
      title="Click to view artifacts in Knowledge Base"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <div className="text-theme-text-secondary text-xs">Artifacts</div>
          <div className="font-semibold text-theme-text text-lg">{total}</div>
        </div>
        <div>
          <div className="text-theme-text-secondary text-xs">Parsed</div>
          <div className="font-semibold text-theme-text text-lg">
            {chunked} ({chunkedPct}%)
          </div>
        </div>
        <div>
          <div className="text-theme-text-secondary text-xs">With Entities</div>
          <div className="font-semibold text-theme-text text-lg">
            {entities} ({entitiesPct}%)
          </div>
        </div>
        <div>
          <div className="text-theme-text-secondary text-xs">Parsing Backlog</div>
          <div className="font-semibold text-theme-text text-lg">
            {total - chunked > 0 ? (
              <span className="text-yellow-700">{total - chunked} pending</span>
            ) : (
              <span className="text-green-600">Complete</span>
            )}
          </div>
        </div>
      </div>
      {(typeof deltaNewEdited === 'number' || typeof deltaKeywordMatched === 'number') && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-theme-text-secondary text-xs">New/Edited (since last)</div>
            <div className="font-semibold text-theme-text text-lg">
              {typeof deltaNewEdited === 'number' ? deltaNewEdited.toLocaleString() : '—'}
            </div>
          </div>
          <div>
            <div className="text-theme-text-secondary text-xs">Needs Processing (keyword match)</div>
            <div className="font-semibold text-theme-text text-lg">
              {typeof deltaKeywordMatched === 'number' ? deltaKeywordMatched.toLocaleString() : '—'}
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="text-theme-text-secondary text-xs">Cutoff</div>
            <div className="font-medium text-theme-text">
              {deltaCutoff ? new Date(deltaCutoff).toLocaleString() : '—'}
            </div>
          </div>
        </div>
      )}
      <div className="text-xs text-theme-text-secondary mt-2 italic">
        Click to view artifacts in Knowledge Base →
      </div>
      {sourceType === 'local_folder' && total - chunked > 0 && (
        <p className="text-xs text-theme-text-secondary mt-2 leading-relaxed">
          “Parsing backlog” means parse / chunk / index is still running in background jobs—not that files failed to
          upload. Large folders can take hours to become fully parsed. Use the “Needs Processing” metric above to see
          how many new/edited files are actually in-scope by keywords.
        </p>
      )}
    </div>
  );
}


export function SourceList() {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterParsed, setFilterParsed] = useState<string>('all'); // 'all', 'parsed', 'not_parsed'
  const [parsedThreshold, setParsedThreshold] = useState<number>(1); // Threshold percentage for "not parsed"
  const [sortBy, setSortBy] = useState<'description' | 'type' | 'last_sync_at'>('last_sync_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [showAddSource, setShowAddSource] = useState(false);
  const [sourceType, setSourceType] = useState<'database' | 'local_folder' | 'sharepoint' | 'gdrive' | 'website'>('database');
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const [sourceViewTabs, setSourceViewTabs] = useState<Record<string, 'list' | 'tree' | 'stats'>>({});
  const [sourceExpandedPaths, setSourceExpandedPaths] = useState<Record<string, Set<string>>>({});
  const [bulkUploadSource, setBulkUploadSource] = useState<{ id: string; name: string; metadata?: Record<string, any> } | null>(null);
  const [fileListPages, setFileListPages] = useState<Record<string, number>>({});
  const [fileListPageSize] = useState(50);
  const [deleteConfirmSource, setDeleteConfirmSource] = useState<{ id: string; name: string; artifactCount: number } | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, {
    jobId: string;
    status: string;
    itemsScanned?: number;
    filesFound?: number;
    filesQueued?: number;
    elapsedSeconds?: number;
    message?: string;
  }>>({});

  const { data: sources, isLoading, refetch } = useQuery({
    queryKey: ['sources', projectId],
    queryFn: () => listSources(projectId!),  // No artifact IDs by default (fast); pass { artifact_ids_limit: N } when needed
    enabled: !!projectId,
    refetchInterval: 30000,
  });

  // Ensure Canadian provincial portal sources exist for the active project (Data Pipeline tab).
  const CANADIAN_PORTAL_SOURCES: Array<{
    integration_type: 'jaggaer' | 'manitoba' | 'saskatchewan' | 'nrc' | 'toronto';
    name: string;
    description: string;
  }> = [
    {
      integration_type: 'jaggaer',
      name: 'Ontario Provincial Portal',
      description: 'Ontario Tenders Portal (Jaggaer ESOP) - Provincial procurement',
    },
    {
      integration_type: 'manitoba',
      name: 'Manitoba Provincial Portal',
      description: 'Manitoba Tendering Portal (MERX) - Provincial procurement',
    },
    {
      integration_type: 'saskatchewan',
      name: 'Saskatchewan Provincial Portal',
      description: 'SaskTenders - Government of Saskatchewan procurement',
    },
    {
      integration_type: 'nrc',
      name: 'Natural Resources Canada',
      description: 'NRCan current funding opportunities and grants',
    },
    {
      integration_type: 'toronto',
      name: 'Toronto Portal',
      description: 'City of Toronto Bids Portal - municipal procurement',
    },
  ];

  useEffect(() => {
    if (!projectId || !sources) return;

    const existingTypes = new Set(
      sources
        .filter((s) => s.type === 'api_integration')
        .map((s) => (s.metadata?.integration_type || s.meta?.integration_type || s.name) as string),
    );

    const missing = CANADIAN_PORTAL_SOURCES.filter(
      (portal) => !existingTypes.has(portal.integration_type),
    );
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const portal of missing) {
        if (cancelled) return;
        try {
          await registerAPIIntegrationSource({
            project_id: projectId,
            name: portal.name,
            description: portal.description,
            integration_type: portal.integration_type,
            sync_frequency: 'daily',
            enabled: true,
            filters: { keywords: ['environmental', 'natural resources', 'procurement'] },
          });
        } catch (error) {
          console.warn(`[SourceList] Failed to register ${portal.integration_type}:`, error);
        }
      }
      if (!cancelled) {
        queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, sources, queryClient]);

  const deleteMutation = useMutation({
    mutationFn: (sourceId: string) => deleteSource(sourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
    },
  });

  // Track syncing per-source so only the clicked button is disabled, not all of them.
  const [syncingSourceIds, setSyncingSourceIds] = useState<Set<string>>(new Set());

  const setSyncingSource = useCallback((sourceId: string, active: boolean) => {
    setSyncingSourceIds(prev => {
      const next = new Set(prev);
      if (active) {
        next.add(sourceId);
      } else {
        next.delete(sourceId);
      }
      return next;
    });
  }, []);

  const triggerSyncForSource = useCallback(
    async (sourceId: string, tableName?: string) => {
      setSyncingSource(sourceId, true);
      try {
        await triggerSync(sourceId, tableName);
        queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
      } finally {
        setSyncingSource(sourceId, false);
      }
    },
    [queryClient, projectId, setSyncingSource],
  );

  // Pre-filter by search, type, and status (before parsed filter to reduce API calls)
  const preFilteredSources = React.useMemo(() => {
    if (!sources) return [];
    return sources.filter((source) => {
      const matchesSearch =
        source.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        source.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        source.id?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || source.type === filterType;
      const matchesStatus = filterStatus === 'all' || source.sync_status === filterStatus;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [sources, searchTerm, filterType, filterStatus]);

  // Fetch sync status only for pre-filtered sources when parsed filter is active
  const sourceIdsForParsedFilter = React.useMemo(() =>
    filterParsed !== 'all' ? preFilteredSources.map(s => s.id) : [],
    [preFilteredSources, filterParsed]
  );

  const syncStatusQueries = useQuery({
    queryKey: ['syncStatuses', sourceIdsForParsedFilter.sort().join(',')],
    queryFn: async () => {
      if (sourceIdsForParsedFilter.length === 0) return {};
      const statuses: Record<string, any> = {};
      // Batch requests with Promise.allSettled to handle errors gracefully
      const results = await Promise.allSettled(
        sourceIdsForParsedFilter.map(async (id) => {
          try {
            const status = await getSyncStatus(id);
            return { id, status };
          } catch (error) {
            console.warn(`Failed to fetch sync status for source ${id}:`, error);
            return { id, status: null };
          }
        })
      );
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.status) {
          statuses[result.value.id] = result.value.status;
        }
      });
      return statuses;
    },
    enabled: filterParsed !== 'all' && sourceIdsForParsedFilter.length > 0,
    staleTime: 60000, // Cache for 1 minute
  });

  const filteredSources = React.useMemo(() => {
    if (!preFilteredSources) return [];

    let filtered = preFilteredSources.filter((source) => {
      // Filter by parsed percentage if enabled
      if (filterParsed !== 'all') {
        const syncStatus = syncStatusQueries.data?.[source.id];
        const artifactStats = syncStatus?.metadata?.artifact_stats;

        if (artifactStats && artifactStats.total_artifacts > 0) {
          const parsedPct = artifactStats.chunked_percent || 0;
          if (filterParsed === 'parsed') {
            return parsedPct >= parsedThreshold;
          } else if (filterParsed === 'not_parsed') {
            return parsedPct < parsedThreshold;
          }
        } else {
          // If no stats available, exclude from "parsed" filter, include in "not_parsed"
          return filterParsed === 'not_parsed';
        }
      }
      return true;
    }).sort((a, b) => {
      const aVal = a[sortBy] ?? '';
      const bVal = b[sortBy] ?? '';

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [preFilteredSources, filterParsed, parsedThreshold, sortBy, sortOrder, syncStatusQueries.data]);

  const paginatedSources = React.useMemo(() => {
    const start = page * pageSize;
    return filteredSources.slice(start, start + pageSize);
  }, [filteredSources, page, pageSize]);

  const totalPages = Math.ceil(filteredSources.length / pageSize);

  // Build file tree using utility function
  const buildFileTreeMemo = useCallback((files: FileNode[]) => buildFileTree(files), []);

  // Initialize expanded paths for a source when it's expanded
  useEffect(() => {
    sources?.forEach((source) => {
      if (source.type === 'local_folder' && source.files && source.files.length > 0 && expandedSources.has(source.id)) {
        // Initialize tab if not set
        if (!sourceViewTabs[source.id]) {
          setSourceViewTabs(prev => ({ ...prev, [source.id]: 'list' }));
        }

        // Initialize expanded paths if not set (expand all by default)
        if (!sourceExpandedPaths[source.id]) {
          const tree = buildFileTreeMemo(source.files);
          const allPaths = new Set<string>();
          const getAllPaths = (nodes: any[]) => {
            nodes.forEach(node => {
              if (node.path) allPaths.add(node.path);
              if (node.name) allPaths.add(node.name);
              if (node.children) getAllPaths(node.children);
            });
          };
          getAllPaths(tree);
          setSourceExpandedPaths(prev => ({ ...prev, [source.id]: allPaths }));
        }
      }
    });
  }, [sources, expandedSources, buildFileTreeMemo, sourceViewTabs, sourceExpandedPaths]);

  // Initialize file tree expansion when source is expanded
  useEffect(() => {
    if (!sources) return;

    sources.forEach(source => {
      if (expandedSources.has(source.id) && source.type === 'local_folder') {
        const allFilesFromMetadata = source.metadata?.files || [];
        if (allFilesFromMetadata.length > 0) {
          const tree = buildFileTreeMemo(allFilesFromMetadata);
          if (!sourceExpandedPaths[source.id] || sourceExpandedPaths[source.id].size === 0) {
            const allPaths = new Set<string>();
            const collectPaths = (nodes: any[]) => {
              nodes.forEach(node => {
                if (node.path) allPaths.add(node.path);
                if (node.name) allPaths.add(node.name);
                if (node.children && node.children.length > 0) {
                  collectPaths(node.children);
                }
              });
            };
            collectPaths(tree);
            setSourceExpandedPaths(prev => ({ ...prev, [source.id]: allPaths }));
          }
        }
      }
    });
  }, [expandedSources, sources, buildFileTreeMemo, sourceExpandedPaths]);

  const handleDelete = (sourceId: string, description: string, artifactCount: number) => {
    setDeleteConfirmSource({ id: sourceId, name: description, artifactCount });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmSource) return;
    await deleteMutation.mutateAsync(deleteConfirmSource.id);
    setDeleteConfirmSource(null);
  };

  const handleTriggerSync = async (
    sourceId: string,
    sourceType?: string,
    sourceMetadata?: Record<string, any>,
    sourceName?: string,
    sourceUri?: string | null,
  ) => {
    // For local_folder sources, browser-registered sources cannot use POST /upload (server scan)
    if (sourceType === 'local_folder') {
      if (isBrowserOnlyLocalFolder(sourceMetadata, sourceUri)) {
        setBulkUploadSource({
          id: sourceId,
          name: sourceName || sourceId,
          metadata: sourceMetadata,
        });
        return;
      }

      // Server-side sources can use the upload endpoint
      try {
        setUploadProgress(prev => ({
          ...prev,
          [sourceId]: {
            jobId: '',
            status: 'starting',
            message: 'Requesting upload job…',
          },
        }));

        const response = await triggerFileUpload(sourceId);

        // Start polling job status for progress updates
        const jobId = response.job_id;
        setUploadProgress(prev => ({
          ...prev,
          [sourceId]: {
            jobId,
            status: 'scanning',
            itemsScanned: 0,
            filesFound: 0,
            filesQueued: 0,
            message: 'Scanning folder (see API/worker logs for full-tree progress)…',
          }
        }));

        let uploadPollInFlight = false;
        // Poll job status every 2 seconds
        const pollInterval = setInterval(async () => {
          if (uploadPollInFlight) return;
          uploadPollInFlight = true;
          try {
            const job = await getJob(jobId);
            const inputData = job.input_data || {};
            const scanStatus = inputData.scan_status;
            const scanProgress = inputData.scan_progress || {};
            const filesPreview = inputData.files_preview || [];
            const filesList = inputData.files || [];
            const filesCount =
              typeof inputData.files_count === 'number'
                ? inputData.files_count
                : Math.max(filesList.length, filesPreview.length, 0);
            const uploadJobIds: string[] = Array.isArray(inputData.upload_job_ids)
              ? inputData.upload_job_ids
              : [];

            let message = 'Processing…';
            if (job.status === 'failed') {
              message = `Failed: ${job.error_message || 'Unknown error'}`;
            } else if (scanStatus === 'scanning') {
              message = `Scanning folder… ${scanProgress.items_scanned?.toLocaleString() || 0} items, ${scanProgress.files_found?.toLocaleString() || 0} files so far (worker may process tens of thousands)`;
            } else if (job.status === 'completed') {
              if (uploadJobIds.length > 0) {
                message = `Folder scan finished (${filesCount.toLocaleString()} files). ${uploadJobIds.length} upload batch job(s) running in Celery—copying to storage continues in the background.`;
              } else {
                message = `Done: ${filesCount.toLocaleString()} file path(s) processed.`;
              }
            } else if (scanStatus === 'complete') {
              message = `Scan recorded: ${filesCount.toLocaleString()} files—upload workers picking up…`;
            } else if (job.status === 'queued') {
              message = `Queued: ${filesCount.toLocaleString()} file(s) in job`;
            } else if (job.status === 'running') {
              message = `Job running… (${filesCount.toLocaleString()} file paths)`;
            } else {
              message = 'Processing…';
            }

            setUploadProgress(prev => ({
              ...prev,
              [sourceId]: {
                jobId,
                status: job.status || 'unknown',
                itemsScanned: scanProgress.items_scanned || 0,
                filesFound: scanProgress.files_found || filesCount || 0,
                filesQueued: filesCount || 0,
                elapsedSeconds: scanProgress.elapsed_seconds,
                message,
              }
            }));

            // Stop polling if job is complete or failed
            if (['completed', 'failed'].includes(job.status || '')) {
              clearInterval(pollInterval);
              queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
              queryClient.invalidateQueries({ queryKey: ['syncStatus', sourceId] });
              // Clear progress after 5 seconds
              setTimeout(() => {
                setUploadProgress(prev => {
                  const newProgress = { ...prev };
                  delete newProgress[sourceId];
                  return newProgress;
                });
              }, 5000);
            }
          } catch (error) {
            console.error('Error polling job status:', error);
            // Continue polling on error
          } finally {
            uploadPollInFlight = false;
          }
        }, 2000);

        // Clear interval after 10 minutes (safety timeout)
        setTimeout(() => clearInterval(pollInterval), 600000);

      } catch (error: any) {
        setUploadProgress(prev => {
          const next = { ...prev };
          delete next[sourceId];
          return next;
        });
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to trigger file upload';
        alert(`Error: ${errorMsg}`);
      }
    } else if (sourceType === 'website' || sourceType === 'sharepoint' || sourceType === 'gdrive') {
      // For SharePoint/GDrive sources, trigger sync and poll for progress
      try {
        const response = await triggerSync(sourceId);

        // Start polling job status for progress updates
        const jobId = response.job_id;
        setUploadProgress(prev => ({
          ...prev,
          [sourceId]: {
            jobId,
            status: 'queued',
            message: 'Sync queued successfully',
          }
        }));

        let syncPollInFlight = false;
        // Poll job status every 2 seconds
        const pollInterval = setInterval(async () => {
          if (syncPollInFlight) return;
          syncPollInFlight = true;
          try {
            const job = await getJob(jobId);
            const inputData = job.input_data || {};
            const outputData = job.output_data || {};

            setUploadProgress(prev => ({
              ...prev,
              [sourceId]: {
                jobId,
                status: job.status || 'unknown',
                message: job.status === 'queued'
                  ? 'Sync queued...'
                  : job.status === 'running'
                    ? `Syncing... ${inputData.processed_files || 0}/${inputData.total_files || 0} files`
                    : job.status === 'completed'
                      ? `Sync complete! ${outputData.artifacts_created || 0} files synced`
                      : job.status === 'failed'
                        ? `Sync failed: ${job.error_message || 'Unknown error'}`
                        : 'Processing...',
              }
            }));

            // Stop polling if job is complete or failed
            if (['completed', 'failed'].includes(job.status || '')) {
              clearInterval(pollInterval);
              queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
              // Clear progress after 5 seconds
              setTimeout(() => {
                setUploadProgress(prev => {
                  const newProgress = { ...prev };
                  delete newProgress[sourceId];
                  return newProgress;
                });
              }, 5000);
            }
          } catch (error) {
            console.error('Error polling job status:', error);
            // Continue polling on error
          } finally {
            syncPollInFlight = false;
          }
        }, 2000);

        // Clear interval after 10 minutes (safety timeout)
        setTimeout(() => clearInterval(pollInterval), 600000);

      } catch (error: any) {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to trigger sync';
        alert(`Error: ${errorMsg}`);
      }
    } else {
      // For database sources, use sync endpoint
      await triggerSyncForSource(sourceId);
    }
  };

  // Group sources by type
  const sourcesByType = React.useMemo(() => {
    const grouped: Record<string, typeof filteredSources> = {};
    filteredSources.forEach(source => {
      const type = source.type || 'unknown';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(source);
    });
    return grouped;
  }, [filteredSources]);

  // Stats
  const activeCount = sources?.filter(s => s.sync_status === 'active' || s.sync_status === 'completed').length || 0;
  const failedCount = sources?.filter(s => s.sync_status === 'failed').length || 0;
  const typeCount = Object.keys(sourcesByType).length;

  if (!projectId) {
    return (
      <Card>
        <p className="text-center py-12 text-theme-text-secondary">Select a project to view sources</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-theme-text">Sources</h1>
            <p className="text-theme-text-secondary mt-1">
              {filteredSources.length} source{filteredSources.length !== 1 ? 's' : ''} across {typeCount} type{typeCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => {
            setSourceType('database');
            setShowAddSource(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Register Source
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center gap-3">
            <Database className="h-8 w-8 text-primary-600" />
            <div>
              <div className="text-2xl font-bold text-theme-text dark:text-black">{sources?.length || 0}</div>
              <div className="text-sm text-theme-text-secondary dark:text-black">Total Sources</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div>
              <div className="text-2xl font-bold text-theme-text dark:text-black">{activeCount}</div>
              <div className="text-sm text-theme-text-secondary dark:text-black">Active</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-pink-50 border-red-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-8 w-8 text-red-600" />
            <div>
              <div className="text-2xl font-bold text-theme-text dark:text-black">{failedCount}</div>
              <div className="text-sm text-theme-text-secondary dark:text-black">Failed</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <div className="flex items-center gap-3">
            <Activity className="h-8 w-8 text-purple-600" />
            <div>
              <div className="text-2xl font-bold text-theme-text dark:text-black">{typeCount}</div>
              <div className="text-sm text-theme-text-secondary dark:text-black">Source Types</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-theme-text-secondary dark:text-black z-10" />
              <Input
                placeholder="Search sources by description, type, or ID..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                className="pl-10 dark:text-black"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-theme-text mb-1">
                <Filter className="inline h-4 w-4 mr-1" />
                Filter by Type
              </label>
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setPage(0);
                }}
                className="w-full px-3 py-2 border border-theme-border rounded-lg bg-theme-paper text-theme-text focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-black"
              >
                <option value="all">All Types</option>
                {Array.from(new Set(sources?.map(s => s.type).filter(Boolean))).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-theme-text mb-1">
                Filter by Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(0);
                }}
                className="w-full px-3 py-2 border border-theme-border rounded-lg bg-theme-paper text-theme-text focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-black"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-theme-text mb-1">
                Filter by Parsed
              </label>
              <div className="flex gap-2">
                <select
                  value={filterParsed}
                  onChange={(e) => {
                    setFilterParsed(e.target.value);
                    setPage(0);
                  }}
                  className="flex-1 px-3 py-2 border border-theme-border rounded-lg bg-theme-paper text-theme-text focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-black"
                >
                  <option value="all">All Sources</option>
                  <option value="parsed">Parsed (≥{parsedThreshold}%)</option>
                  <option value="not_parsed">Not Parsed (&lt;{parsedThreshold}%)</option>
                </select>
                {filterParsed !== 'all' && (
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={parsedThreshold}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setParsedThreshold(Math.max(0, Math.min(100, val)));
                      setPage(0);
                    }}
                    className="w-20 px-2 py-2 border border-theme-border rounded-lg bg-theme-paper text-theme-text focus:ring-2 focus:ring-blue-500 text-sm dark:text-black"
                    placeholder="%"
                  />
                )}
              </div>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-theme-text mb-1">
                Sort By
              </label>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="flex-1 px-3 py-2 border border-theme-border rounded-lg bg-theme-paper text-theme-text focus:ring-2 focus:ring-blue-500 focus:outline-none dark:text-black"
                >
                  <option value="last_sync_at">Last Sync</option>
                  <option value="description">Description</option>
                  <option value="type">Type</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border border-theme-border rounded-lg bg-theme-paper text-theme-text hover:bg-theme-surface-hover"
                  title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Sources Grouped by Type */}
      {Object.entries(sourcesByType).map(([type, sourcesOfType]) => (
        <Card key={type} title={`${type
          ?.replace(/_/g, ' ')
          .replace(/\b(api|rfp|ui|ux|id)\b/gi, (m) => m.toUpperCase())
          .replace(/\b\w/g, (l) => l.toUpperCase())
        } Sources (${sourcesOfType.length})`}>
          <div className="grid grid-cols-1 gap-3">
            {sourcesOfType.slice(page * pageSize, (page + 1) * pageSize).map((source) => {
              const statusColors = {
                active: 'bg-green-100 text-green-800',
                completed: 'bg-blue-100 text-blue-800',
                pending: 'bg-yellow-100 text-yellow-800',
                failed: 'bg-red-100 text-red-800',
              };
              const statusIcons = {
                active: CheckCircle,
                completed: CheckCircle,
                pending: Clock,
                failed: AlertCircle,
              };
              const apiDisplay =
                source.type === 'api_integration' ? getApiIntegrationDisplay(source) : null;
              const supportsManualSync = source.type !== 'upload';
              const effectiveStatus = apiDisplay?.statusLabel ?? source.sync_status;
              const StatusIcon =
                effectiveStatus === 'failed'
                  ? AlertCircle
                  : effectiveStatus === 'queued' || effectiveStatus === 'pending' || effectiveStatus === 'misconfigured'
                    ? Clock
                    : effectiveStatus === 'running'
                      ? Activity
                      : effectiveStatus === 'completed' || effectiveStatus === 'active'
                        ? CheckCircle
                        : statusIcons[source.sync_status as keyof typeof statusIcons] || Activity;

              const isExpanded = expandedSources.has(source.id);
              const metadata = source.metadata || {};
              const fileExtensions = metadata.file_extensions || [];
              const watchForChanges = metadata.watch_for_changes ?? true;
              const recursive = metadata.recursive ?? true;
              const excludePatterns = metadata.exclude_patterns || [];

              // Get all files from metadata (not limited to 50)
              const allSourceFiles = source.metadata?.files || source.files || [];

              const localUpload = source.type === 'local_folder' ? uploadProgress[source.id] : undefined;
              const isLocalUploading = Boolean(localUpload);
              const localUploadTerminal =
                localUpload?.status === 'completed' || localUpload?.status === 'failed';

              return (
                <div key={source.id} className="border border-theme-border rounded-lg hover:shadow-md transition-shadow bg-theme-paper">
                  <div className="p-4">
                    {isLocalUploading && (
                      <div
                        className={`mb-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
                          localUpload?.status === 'failed'
                            ? 'border-red-200 bg-red-50 dark:bg-red-950/40'
                            : localUploadTerminal
                              ? 'border-green-200 bg-green-50 dark:bg-green-950/30'
                              : 'border-blue-200 bg-blue-50 dark:bg-blue-950/40'
                        }`}
                        role="status"
                        aria-live="polite"
                      >
                        {localUpload?.status === 'failed' ? (
                          <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                        ) : localUploadTerminal ? (
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <RefreshCw className="h-4 w-4 text-blue-600 animate-spin flex-shrink-0 mt-0.5" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-theme-text">Upload in progress</div>
                          <div className="text-theme-text-secondary text-xs mt-0.5 break-words">{localUpload?.message}</div>
                          {(localUpload?.itemsScanned ?? 0) > 0 && (
                            <div className="text-xs text-theme-text-secondary mt-1">
                              Scanned {localUpload?.itemsScanned?.toLocaleString()} items
                              {(localUpload?.filesFound ?? 0) > 0 && (
                                <> · {localUpload?.filesFound?.toLocaleString()} files found so far</>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          {source.type === 'local_folder' ? <Folder className="h-6 w-6 text-blue-600" /> :
                            source.type === 'sharepoint' || source.type === 'gdrive' ? <Cloud className="h-6 w-6 text-blue-600" /> :
                              source.type === 'website' ? <Globe className="h-6 w-6 text-teal-600" /> :
                                <Database className="h-6 w-6 text-blue-600" />}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-theme-text">
                                {source.name || source.description || source.id}
                              </h3>
                              <button
                                onClick={() => {
                                  const newExpanded = new Set(expandedSources);
                                  if (isExpanded) {
                                    newExpanded.delete(source.id);
                                  } else {
                                    newExpanded.add(source.id);
                                  }
                                  setExpandedSources(newExpanded);
                                }}
                                className="text-theme-text-secondary hover:text-theme-text transition-colors"
                                title={isExpanded ? 'Collapse details' : 'Expand details'}
                              >
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                              </button>
                            </div>
                            <p className="text-sm text-theme-text-secondary">
                              {source.file_path && (
                                <span className="font-mono">{source.file_path}</span>
                              )}
                              {!source.file_path && source.uri && (
                                <span className="font-mono">{source.uri}</span>
                              )}
                              {!source.file_path && !source.uri && (
                                <span className="font-mono">{source.id.substring(0, 24)}...</span>
                              )}
                            </p>
                            {source.description && source.name && (
                              <p className="text-xs text-theme-text-secondary mt-1">{source.description}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="text-theme-text-secondary text-xs">Type</div>
                            <div className="font-medium text-theme-text">{source.type}</div>
                          </div>
                          <div>
                            <div className="text-theme-text-secondary text-xs">Region</div>
                            <div className="font-medium text-theme-text">{source.source_region || '-'}</div>
                          </div>
                          <div>
                            <div className="text-theme-text-secondary text-xs">Last Sync</div>
                            <div className="font-medium text-theme-text">
                              {apiDisplay
                                ? apiDisplay.lastSyncLabel
                                : source.sync_status === 'queued' && source.last_sync_at
                                  ? 'Queued now'
                                  : source.last_sync_at
                                    ? new Date(source.last_sync_at).toLocaleString()
                                    : 'Never'}
                            </div>
                          </div>
                          <div>
                            <div className="text-theme-text-secondary text-xs">Status</div>
                            <div className="flex items-center gap-1">
                              <StatusIcon className="h-4 w-4" />
                              <Badge variant={
                                apiDisplay
                                  ? apiDisplay.badgeVariant
                                  : source.sync_status === 'active' || source.sync_status === 'completed' ? 'success' :
                                    source.sync_status === 'queued' || source.sync_status === 'pending' ? 'warning' :
                                      source.sync_status === 'running' ? 'default' :
                                        source.sync_status === 'failed' ? 'error' :
                                          'default'
                              }>
                                {apiDisplay
                                  ? apiDisplay.statusLabel
                                  : source.sync_status === 'pending'
                                    ? 'queued'
                                    : source.sync_status || 'unknown'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        {apiDisplay?.hint && (
                          <p className="mt-2 text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded px-2 py-1.5">
                            {apiDisplay.hint}
                          </p>
                        )}
                        {source.type === 'local_folder' &&
                          (!source.sync_status || source.sync_status === 'unknown') &&
                          (source.file_count ?? 0) > 0 && (
                          <p className="mt-2 text-xs text-theme-text-secondary">
                            Status may show &quot;unknown&quot; until the first folder sync/upload job runs. Ensure the
                            Celery worker is running (queues: default, ingest, sync) so uploads and parsing can proceed.
                          </p>
                        )}

                        {/* Artifact Statistics for file-based and website sources */}
                        {(source.type === 'sharepoint' || source.type === 'gdrive' || source.type === 'local_folder' || source.type === 'website') && (
                          <SourceArtifactStats sourceId={source.id} sourceType={source.type} />
                        )}

                        {/* File statistics for file-based sources - Collapsed View */}
                        {source.type === 'local_folder' && (source.file_count ?? 0) > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                              <div>
                                <div className="text-theme-text-secondary text-xs">Total Files</div>
                                <div className="font-semibold text-theme-text text-lg">{source.file_count}</div>
                              </div>
                              <div>
                                <div className="text-theme-text-secondary text-xs">Total Size</div>
                                <div className="font-semibold text-theme-text text-lg">
                                  {formatFileSize(source.total_size_bytes ?? 0)}
                                </div>
                              </div>
                              <div>
                                <div className="text-theme-text-secondary text-xs">File Types</div>
                                <div className="font-semibold text-theme-text text-lg">
                                  {Object.keys(source.files_by_type || {}).length}
                                </div>
                              </div>
                              <div>
                                <div className="text-theme-text-secondary text-xs">Uploaded</div>
                                <div className="font-semibold text-theme-text text-sm flex items-center gap-1">
                                  {source.total_artifact_count !== undefined
                                    ? <>{source.total_artifact_count.toLocaleString()} artifact{source.total_artifact_count !== 1 ? 's' : ''}</>
                                    : source.artifact_ids && source.artifact_ids.length > 0
                                      ? `${source.artifact_ids.length} uploaded`
                                      : 'Pending upload'}
                                  {source.has_more_artifacts && (
                                    <Badge variant="default" className="text-xs">
                                      {source.artifact_ids?.length || 0} shown
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Files by Type - Quick View */}
                            {source.files_by_type && Object.keys(source.files_by_type).length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {Object.entries(source.files_by_type).map(([type, count]) => (
                                  <Badge key={type} variant="default" className="text-xs">
                                    {type}: {count as number}
                                  </Badge>
                                ))}
                              </div>
                            )}

                            {/* Preview of first few files */}
                            {source.files && source.files.length > 0 && (
                              <div className="mt-3 pt-3 border-t">
                                <div className="text-theme-text-secondary text-xs mb-2">Sample Files ({Math.min(3, source.files.length)} of {source.files.length}):</div>
                                <div className="space-y-1">
                                  {source.files.slice(0, 3).map((file, idx) => (
                                    <div key={idx} className="flex items-center gap-2 text-xs">
                                      <File className="h-3 w-3 text-theme-text-secondary flex-shrink-0" />
                                      <span className="text-theme-text truncate flex-1" title={file.filename}>
                                        {file.filename}
                                      </span>
                                      <span className="text-theme-text-secondary flex-shrink-0">{formatFileSize(file.file_size_bytes)}</span>
                                    </div>
                                  ))}
                                  {source.files.length > 3 && (
                                    <div className="text-xs text-theme-text-secondary italic pt-1">
                                      + {source.files.length - 3} more files (expand to see all)
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Detailed progress (duplicate summary removed — banner at top shows status) */}
                      {uploadProgress[source.id] &&
                        source.type !== 'local_folder' &&
                        (() => {
                          const progress = uploadProgress[source.id]!;
                          return (
                            <div className="mt-3 pt-3 border-t">
                              <div className="flex items-center gap-3 mb-2">
                                <Activity className="h-4 w-4 text-blue-600 animate-pulse" />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-theme-text">
                                    {progress.message}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                      {/* Actions */}
                      <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              handleTriggerSync(
                                source.id,
                                source.type,
                                source.metadata,
                                source.name || source.description || source.id,
                                source.uri,
                              )}
                            disabled={syncingSourceIds.has(source.id) || !!uploadProgress[source.id]}
                            title={source.type === 'local_folder' ? 'Trigger file upload' : 'Trigger sync'}
                          >
                            {syncingSourceIds.has(source.id) ||
                            (isLocalUploading && !localUploadTerminal) ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Play className="h-4 w-4 mr-2" />
                            )}
                            {source.type === 'local_folder'
                              ? isLocalUploading && !localUploadTerminal
                                ? 'Uploading…'
                                : 'Upload'
                              : syncingSourceIds.has(source.id)
                                ? 'Syncing…'
                                : 'Sync'}
                          </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            // Fetch and display sync status inline
                            try {
                              const status = await getSyncStatus(source.id);
                              let statusMessage = `Sync Status for ${source.name || source.description || source.id}:\n\n`;
                              statusMessage += `Type: ${status.source_type}\n`;
                              statusMessage += `Status: ${status.sync_status}\n`;
                              statusMessage += `Last Sync: ${status.last_sync_at ? new Date(status.last_sync_at).toLocaleString() : 'Never'}\n`;

                              // Show type-specific information
                              if (source.type === 'local_folder') {
                                statusMessage += `\nFiles: ${status.total_files ?? 0}\n`;
                                statusMessage += `Total Size: ${status.total_size ? `${(status.total_size / 1024 / 1024).toFixed(2)} MB` : 'N/A'}\n`;
                                if (status.files && status.files.length > 0) {
                                  statusMessage += `\nRecent files:\n`;
                                  status.files.slice(0, 5).forEach((file) => {
                                    statusMessage += `  - ${file.file_name} (${file.sync_status})\n`;
                                  });
                                }
                              } else if (['postgres', 'mysql', 'mssql'].includes(source.type)) {
                                statusMessage += `Tables: ${status.total_tables ?? 0}\n`;
                                statusMessage += `Rows Synced: ${status.total_rows_synced ?? 0}\n`;
                                statusMessage += `PII Masked: ${status.total_pii_masked ?? 0}\n`;
                              }

                              alert(statusMessage);
                            } catch (error: any) {
                              const errorMsg = error.response?.data?.detail || error.message || 'Failed to fetch sync status';
                              alert(`Error: ${errorMsg}`);
                            }
                          }}
                          title="View sync status"
                        >
                          <Activity className="h-4 w-4 mr-2" />
                          Status
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(source.id, source.description || source.id, source.artifact_ids?.length || 0)}
                          disabled={deleteMutation.isPending}
                          title="Delete source"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t space-y-4">
                        {/* Source Configuration */}
                        <div>
                          <h4 className="text-sm font-semibold text-theme-text mb-2">Configuration</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            {source.type === 'local_folder' && (
                              <>
                                <div>
                                  <div className="text-theme-text-secondary text-xs">Watch for Changes</div>
                                  <div className="font-medium text-theme-text">{watchForChanges ? 'Yes' : 'No'}</div>
                                </div>
                                <div>
                                  <div className="text-theme-text-secondary text-xs">Recursive</div>
                                  <div className="font-medium text-theme-text">{recursive ? 'Yes' : 'No'}</div>
                                </div>
                                <div>
                                  <div className="text-theme-text-secondary text-xs">File Extensions</div>
                                  <div className="font-medium text-theme-text">{fileExtensions.length} selected</div>
                                </div>
                                <div>
                                  <div className="text-theme-text-secondary text-xs">Exclude Patterns</div>
                                  <div className="font-medium text-theme-text">{excludePatterns.length} patterns</div>
                                </div>
                              </>
                            )}
                            <div>
                              <div className="text-theme-text-secondary text-xs">Created</div>
                              <div className="font-medium text-theme-text">
                                {new Date(source.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>

                          {/* File Extensions */}
                          {fileExtensions.length > 0 && (
                            <div className="mt-3">
                              <div className="text-theme-text-secondary text-xs mb-2">Selected File Extensions:</div>
                              <div className="flex flex-wrap gap-2">
                                {fileExtensions.map((ext: string) => (
                                  <Badge key={ext} variant="default" className="text-xs">
                                    {ext} {EXTENSION_DESCRIPTIONS[ext] ? `(${EXTENSION_DESCRIPTIONS[ext]})` : ''}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Exclude Patterns */}
                          {excludePatterns.length > 0 && (
                            <div className="mt-3">
                              <div className="text-theme-text-secondary text-xs mb-2">Exclude Patterns:</div>
                              <div className="flex flex-wrap gap-2">
                                {excludePatterns.map((pattern: string, idx: number) => (
                                  <Badge key={idx} variant="default" className="text-xs font-mono border border-gray-300">
                                    {pattern}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* SAP Ariba: steps to renew session when sync fails with 403 */}
                        {source.type === 'api_integration' && (source.metadata?.integration_type || source.name) === 'sap_ariba' && (
                          <div className="mt-4 pt-4 border-t">
                            <h4 className="text-sm font-semibold text-theme-text mb-2 flex items-center gap-2">
                              <HelpCircle className="h-4 w-4 text-amber-500" />
                              Renew SAP Ariba session (if sync fails with 403)
                            </h4>
                            <p className="text-xs text-theme-text-secondary mb-2">
                              SAP Ariba Discovery 2.0 uses session cookies and a CSRF token that expire. If sync returns &quot;403&quot; or &quot;authentication failed&quot;, renew the session:
                            </p>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
                              {SAP_ARIBA_RENEWAL_STEPS}
                            </div>
                          </div>
                        )}

                        {/* File List / File Tree / Statistics Tabs */}
                        {source.type === 'local_folder' && allSourceFiles.length > 0 && (
                          <SourceFileTabs
                            sourceId={source.id}
                            files={allSourceFiles}
                            currentTab={sourceViewTabs[source.id] || 'list'}
                            onTabChange={(tab) => setSourceViewTabs(prev => ({ ...prev, [source.id]: tab }))}
                            fileListPage={fileListPages[source.id] || 0}
                            onFileListPageChange={(page) => setFileListPages(prev => ({ ...prev, [source.id]: page }))}
                            fileListPageSize={fileListPageSize}
                            expandedTreePaths={sourceExpandedPaths[source.id] || new Set()}
                            onExpandedTreePathsChange={(updater) => {
                              setSourceExpandedPaths(prev => {
                                const newPaths = typeof updater === 'function'
                                  ? updater(prev[source.id] || new Set())
                                  : updater;
                                return { ...prev, [source.id]: newPaths };
                              });
                            }}
                            buildFileTree={buildFileTreeMemo}
                            formatFileSize={formatFileSize}
                            supportedExtensions={SUPPORTED_EXTENSIONS}
                            extensionDescriptions={EXTENSION_DESCRIPTIONS}
                            fileExtensions={fileExtensions}
                            source={source}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ))}

      {filteredSources.length === 0 && !isLoading && (
        <Card>
          <div className="text-center py-12">
            <Database className="h-12 w-12 text-theme-text-secondary mx-auto mb-4" />
            <p className="text-theme-text-secondary mb-4">No sources configured</p>
            <Button onClick={() => setShowAddSource(true)}>
              <Plus className="h-4 w-4" />
              Register Your First Source
            </Button>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="text-sm text-theme-text-secondary">
              Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, filteredSources.length)} of {filteredSources.length}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-1 rounded ${page === pageNum
                        ? 'bg-primary-600 text-white'
                        : 'bg-theme-surface-hover text-theme-text hover:bg-theme-border-light'
                      }`}
                  >
                    {pageNum + 1}
                  </button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Add Source Modal */}
      {showAddSource && (
        <AddSourceModal
          projectId={projectId!}
          sourceType={sourceType}
          onSourceTypeChange={setSourceType}
          onClose={() => {
            setShowAddSource(false);
            setSourceType('database');
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
            setShowAddSource(false);
            setSourceType('database');
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteSourceConfirmationModal
        sourceName={deleteConfirmSource?.name || ''}
        artifactCount={deleteConfirmSource?.artifactCount || 0}
        isOpen={!!deleteConfirmSource}
        onClose={() => setDeleteConfirmSource(null)}
        onConfirm={confirmDelete}
        isDeleting={deleteMutation.isPending}
      />

      {/* Bulk Upload Modal */}
      {bulkUploadSource && (() => {
        const source = sources?.find(s => s.id === bulkUploadSource.id);

        // Get all files from metadata (not limited to 50 like the API response)
        // The metadata.files array contains all files from registration
        const allFilesFromMetadata = source?.metadata?.files || [];

        // Note: If source has many artifacts (indicated by has_more_artifacts),
        // the metadata.files should contain the full list from registration.
        // The artifact_ids array is paginated for performance.

        // If metadata doesn't have files, fall back to the API response (limited to 50)
        const filesToUse = allFilesFromMetadata.length > 0
          ? allFilesFromMetadata
          : (source?.files || []);

        // Merge with uploaded artifacts to get artifact_ids
        const filesWithArtifacts = filesToUse.map((fileData: any) => {
          // Check if this file has been uploaded by matching filename
          const uploadedFile = source?.files?.find(f =>
            f.filename === (fileData.filename || fileData.name)
          );
          return {
            filename: fileData.filename || fileData.name || '',
            path: fileData.path || fileData.filename || fileData.name || '',
            file_size_bytes: fileData.file_size_bytes || fileData.size || 0,
            artifact_type: fileData.artifact_type || 'unknown',
            artifact_id: uploadedFile?.artifact_id || fileData.artifact_id || null,
            created_at: uploadedFile?.created_at || fileData.created_at || null,
          };
        });

        return (
          <BulkUploadModal
            projectId={projectId!}
            sourceId={bulkUploadSource.id}
            sourceName={bulkUploadSource.name}
            sourceFiles={filesWithArtifacts}
            sourceMetadata={bulkUploadSource.metadata}
            onClose={() => setBulkUploadSource(null)}
          />
        );
      })()}
    </div>
  );
}
